import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./qualitative.css";
import Table from "../../table";
import { useAuth } from "../../../context/auth-context";
import { useAsync } from "../../../utils/use-async";
import { client } from "../../../utils/api-client";
import { Button, Form, Popconfirm, message } from "antd";
import { Link } from "react-router-dom";
import TaskList from "../qualitative-task-list";
import EditList from "../qualitative-edit";
import exportFromJSON from 'export-from-json';
import EmailDateFilters from "./filter";

const AllQualitative = () => {
  const { run, data, isError, error } = useAsync();
  const { user } = useAuth();

  const [usersLoading, setUsersLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [taskModal, setTaskModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [taskList, setTaskList] = useState([]);
  const [editList, setEditList] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });
  const [filters, setFilters] = useState({
    user: null,
    fromDate: null,
    toDate: null,
  });
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };
  const approveRecord = useCallback(async (val) => {
    try {
      const res = await run(client("qualitative/tasks/approve", { body: val, method: "PUT" }));
      if (res.success) fetchRecords(pagination, filters);
    } catch (err) {
      console.error("Error approving record:", err);
    }
  }, [run, pagination, filters]);

  const columns = useMemo(() => [
    {
      title: "Effective Date",
      dataIndex: "effectiveDate",
      key: "effectiveDate",
      width: 100,
      ellipsis: true,
      render: (text) => <>{text.split("T")[0]}</>,
    },
    {
      title: "Approval Status",
      dataIndex: "managerApproval",
      key: "managerApproval",
      width: 100,
      ellipsis: true,
      render: (value) => <>{value.toString() === "false" ? "Pending" : "Approved"}</>,
    },
    {
      title: "Total Time",
      dataIndex: "totalHours",
      key: "totalHours",
      width: 100,
      ellipsis: true,
      render: (text) => <div style={{ whiteSpace: "pre-wrap" }}>{text}</div>,
    },
    {
      title: "Manager Remarks",
      dataIndex: "managerRemark",
      key: "managerRemark",
      width: 100,
      ellipsis: true,
      render: (text) => <div style={{ whiteSpace: "pre-wrap" }}>{text}</div>,
    },
    {
      title: "User",
      dataIndex: "addedBy",
      key: "addedBy",
      width: 100,
      ellipsis: true,
      render: (addedBy) => <>{addedBy?.email}</>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      ellipsis: true,
      render: (_, record) => (
        <>
          <Button
            type="link"
            onClick={() => {
              setTaskList(record?.taskList || []);
              setTaskModal(true);
            }}
          >
            View Task List
          </Button>

          <Button
            type="link"
            onClick={() => {
              setEditList(record);
              setEditModal(true);
            }}
          >
            Edit Task List
          </Button>

          <Popconfirm
            title="Sure to approve?"
            onConfirm={() => approveRecord(record)}
          >
            <Button type="link">Approve Task</Button>
          </Popconfirm>
        </>
      ),
    },
  ], [approveRecord]);

  const transformTableData = useCallback(
    (data = []) =>
      data.map((user, index) => ({
        key: index,
        ...user,
        sr: index + 1,
      })),
    []
  );

  const fetchRecords = useCallback(
    (currentPagination = pagination, currentFilters = filters) => {
      setUsersLoading(true);

      run(
        client(
          `qualitative/all/manager?page=${currentPagination.current}&pageSize=${currentPagination.pageSize}&user=${currentFilters.user}&fromDate=${currentFilters.fromDate}&toDate=${currentFilters.toDate}`
        )
      )
        .then(() => {
          setUsersLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching records:", err);
        });
    },
    [run]
  );

useEffect(() => {
  fetchRecords(pagination, filters);
}, [filters.user, filters.fromDate, filters.toDate]);
;

  useEffect(() => {
    if (data?.message === "Fetched Qualiatative Task") {
      setRecords(transformTableData(data?.qualitativeRecords || []));
      setUsersLoading(false);
    }
  }, [data, transformTableData]);

  useEffect(() => {
    if (isError && error) {
      message.error(error.message);
    }
  }, [isError, error]);

  // Fetch records when component mounts or when taskModal/editModal changes
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  

  // Memoize the handlePrint function
  const handlePrint = useCallback(() => {
    if (!data?.qualitativeRecords) {
      message.error("No data available to export");
      return;
    }
    
    const fileName = "Qualitative_Task_Approved";
    const exportType = "xls";
    const dataToExport = data.qualitativeRecords.map(record => {
      const tasksFormatted = record.taskList.map(task => {
        return `${task.title} (${task.timeSpent.hours}h ${task.timeSpent.minutes}m)`;
      }).join(', ');
  
      return {
        'Effective Date': new Date(record.effectiveDate).toLocaleDateString(),
        'Approval Status': record.managerApproval ? 'Approved' : 'Pending',
        'Total Time': record.totalHours,
        'User': record.addedBy.email,
        'Task Details': tasksFormatted
      };
    });

    exportFromJSON({ data: dataToExport, fileName, exportType });
  }, [data]);

  return (
    <div className="manage--wrapper">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <h1 style={{ marginBottom: 0 }}>Qualitative Projects</h1>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <Button onClick={handlePrint} style={{ color: "#1890ff" }}>
              Download All Task
            </Button>
            <Link to={`/qualitative-task/approved`}>
              <Button style={{ color: "#1890ff" }}>View Approved Task</Button>
            </Link>
          </div>
        </div>

        <EmailDateFilters onFiltersChange={handleFiltersChange} />
      </div>

      <Table
        loading={usersLoading}
        data={records}
        columns={columns}
        size="middle"
      />

      {taskModal && (
        <TaskList
          modal={taskModal}
          setModal={setTaskModal}
          records={taskList}
        />
      )}

      {editModal && editList && (
        <EditList
          modal={editModal}
          setModal={setEditModal}
          records={editList}
          fetchData={() => fetchRecords(pagination, filters)}
        />
      )}
    </div>
  );
};

export default React.memo(AllQualitative);
